import { useCallback, useEffect, useState } from "react";

import toastError from "../../errors/toastError";

// A lista de atendentes de uma janela de transferir: busca, paginação e
// estado de carregamento, numa consulta por vez.
//
// Toda a complicação que este hook substitui vinha de haver TRÊS caminhos de
// requisição no componente (lista inicial, busca e "carregar mais"), cada um
// com o seu jeito de decidir se a resposta ainda valia. Aqui existe UMA
// consulta, descrita por (busca, página), e um `AbortController` por consulta:
// mudou a pergunta, a anterior é cancelada — não há resposta atrasada para
// chegar fora de hora, nem contador de geração para acertar.
//
// Quem busca é a `fonte`: a lista de atendentes da empresa, ou os candidatos
// de uma chamada. Para o hook as duas são a mesma coisa — recebem
// `{ searchParam, pageNumber }` e devolvem `{ users, count, hasMore }`.
const MINIMO_PARA_BUSCAR = 3;
const ESPERA_DA_DIGITACAO = 500;

const useListaDeAtendentes = ({ aberto, fonte }) => {
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState({ items: [], total: 0, hasMore: false });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  // Abriu, fechou, ou mudou A FONTE: a pergunta volta ao começo. Ajuste na
  // renderização, e não em efeito, porque um efeito só correria DEPOIS de um
  // quadro pintado com o resultado da pergunta anterior.
  //
  // A fonte entra aqui porque ela É a pergunta: a janela da chamada troca de
  // `callId` sem fechar, e continuar na página 2 emendaria a lista de uma
  // ligação na da outra.
  const [anterior, setAnterior] = useState({ aberto, fonte });

  if (aberto !== anterior.aberto || fonte !== anterior.fonte) {
    setAnterior({ aberto, fonte });
    setBusca("");
    setPagina(1);
    setDados({ items: [], total: 0, hasMore: false });
    setErro(false);
    setCarregando(aberto);
  }

  // Menos de três letras não é busca: é a lista de quem está online.
  const termo = busca.trim().length >= MINIMO_PARA_BUSCAR ? busca.trim() : "";

  useEffect(() => {
    if (!aberto) return undefined;

    const controle = new AbortController();

    // A espera da digitação faz parte da MESMA consulta: cancelar o relógio no
    // cleanup é o que evita disparar a busca de um termo que já mudou.
    const relogio = setTimeout(
      async () => {
        setCarregando(true);
        setErro(false);

        try {
          const resposta = await fonte(
            { searchParam: termo, pageNumber: pagina },
            controle.signal
          );

          if (controle.signal.aborted) return;

          setDados(atual => ({
            // Página 1 troca a lista; as seguintes emendam.
            items:
              pagina === 1
                ? resposta.users || []
                : [...atual.items, ...(resposta.users || [])],
            total: Number(resposta.count) || 0,
            hasMore: Boolean(resposta.hasMore)
          }));
        } catch (err) {
          if (controle.signal.aborted) return;

          setErro(true);

          // Falha na página 2 NÃO apaga a 1: quem clicou em "carregar mais"
          // perdia a lista inteira por causa de uma página que não veio, e o
          // que restava era uma janela vazia sem explicação. A página em curso
          // também não anda para trás — `tentarDeNovo` repete ESTA, e o
          // sucesso emenda onde parou.
          if (pagina === 1) setDados({ items: [], total: 0, hasMore: false });

          toastError(err);
        } finally {
          // A resposta abortada não desliga o indicador de uma consulta que
          // já é outra: quem abortou já ligou o dela.
          if (!controle.signal.aborted) setCarregando(false);
        }
      },
      termo ? ESPERA_DA_DIGITACAO : 0
    );

    return () => {
      clearTimeout(relogio);
      controle.abort();
    };
  }, [aberto, termo, pagina, fonte, tentativa]);

  const carregarMais = useCallback(() => setPagina(p => p + 1), []);

  const buscar = useCallback(valor => {
    setBusca(valor);
    // Termo novo recomeça a paginação: emendar a página 2 de outra pergunta
    // misturaria duas listas.
    setPagina(1);
  }, []);

  // Tentar de novo repete a MESMA pergunta: é uma dependência do efeito, então
  // não há função solta mexendo no estado de uma janela que já fechou.
  const tentarDeNovo = useCallback(() => setTentativa(n => n + 1), []);

  return {
    busca,
    buscar,
    termo,
    items: dados.items,
    total: dados.total,
    hasMore: dados.hasMore,
    carregando,
    carregandoMais: carregando && pagina > 1,
    carregarMais,
    erro,
    tentarDeNovo
  };
};

export default useListaDeAtendentes;
